import PopupPagination from "../../../component/Pagination/popupPagination";
import {
  SellerMemberBingoItemResponse,
  SellerMemberMissionItemResponse,
  SellerPubMissionMemberStatsItemResponse,
} from "../../../api/pub-mission-member";
import * as S from "../styles";

interface ListFilter {
  name: string;
  startDate: string;
  endDate: string;
  page: number;
}

interface FilteredListSectionProps<
  T extends { id: number; name: string; progressLabel: string },
> {
  title: string;
  list: T[];
  total: number;
  limit: number;
  filter: ListFilter;
  onFilterChange: (filter: ListFilter) => void;
  selectedId?: number | null;
  onSelect: (item: T) => void;
  showFilter?: boolean;
}

const FilteredListSection = <
  T extends { id: number; name: string; progressLabel: string },
>({
  title,
  list,
  total,
  limit,
  filter,
  onFilterChange,
  selectedId,
  onSelect,
  showFilter = true,
}: FilteredListSectionProps<T>) => {
  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <S.SubSection>
      <S.SubSectionTitle>{title}</S.SubSectionTitle>
      {showFilter && (
        <S.FilterBlock>
          <S.FilterNameRow>
            <input
              type="text"
              placeholder="이름 검색"
              value={filter.name}
              onChange={(e) =>
                onFilterChange({ ...filter, name: e.target.value, page: 1 })
              }
              onKeyUp={(e) => {
                if (e.key === "Enter") {
                  onFilterChange({ ...filter, page: 1 });
                }
              }}
            />
            <S.GrayButton
              type="button"
              onClick={() => onFilterChange({ ...filter, page: 1 })}
            >
              검색
            </S.GrayButton>
          </S.FilterNameRow>

          <S.FilterDateRow>
            <S.FilterDateField>
              <input
                id={`startDate-${title}`}
                name="startDate"
                type="date"
                aria-label="시작일"
                value={filter.startDate}
                onChange={(e) =>
                  onFilterChange({
                    ...filter,
                    startDate: e.target.value,
                    page: 1,
                  })
                }
              />
            </S.FilterDateField>
            <S.FilterDateSeparator>~</S.FilterDateSeparator>
            <S.FilterDateField>
              <input
                id={`endDate-${title}`}
                name="endDate"
                type="date"
                aria-label="종료일"
                value={filter.endDate}
                min={filter.startDate || undefined}
                onChange={(e) =>
                  onFilterChange({
                    ...filter,
                    endDate: e.target.value,
                    page: 1,
                  })
                }
              />
            </S.FilterDateField>
          </S.FilterDateRow>
        </S.FilterBlock>
      )}

      {list.length === 0 ? (
        <S.SubText>항목이 없습니다.</S.SubText>
      ) : (
        list.map((item) => (
          <S.ListItem
            key={item.id}
            type="button"
            $selected={selectedId === item.id}
            onClick={() => onSelect(item)}
          >
            <div className="name">{item.name}</div>
            <div className="progress">{item.progressLabel}</div>
          </S.ListItem>
        ))
      )}

      {totalPages > 1 && (
        <S.PaginationBox>
          <PopupPagination
            currentPage={filter.page}
            totalPages={totalPages}
            onPageChange={(page) => onFilterChange({ ...filter, page })}
          />
        </S.PaginationBox>
      )}
    </S.SubSection>
  );
};

interface MemberDetailPanelProps {
  member: SellerPubMissionMemberStatsItemResponse;
  inProgressMissions: SellerMemberMissionItemResponse[];
  inProgressMissionTotal: number;
  inProgressMissionFilter: ListFilter;
  onInProgressMissionFilterChange: (filter: ListFilter) => void;
  completedMissions: SellerMemberMissionItemResponse[];
  completedMissionTotal: number;
  completedMissionFilter: ListFilter;
  onCompletedMissionFilterChange: (filter: ListFilter) => void;
  inProgressBingos: SellerMemberBingoItemResponse[];
  inProgressBingoTotal: number;
  inProgressBingoFilter: ListFilter;
  onInProgressBingoFilterChange: (filter: ListFilter) => void;
  completedBingos: SellerMemberBingoItemResponse[];
  completedBingoTotal: number;
  completedBingoFilter: ListFilter;
  onCompletedBingoFilterChange: (filter: ListFilter) => void;
  listLimit: number;
  selectedMissionId?: number | null;
  selectedBingoId?: number | null;
  onSelectMission: (item: SellerMemberMissionItemResponse) => void;
  onSelectBingo: (id: number) => void;
}

const MemberDetailPanel = ({
  member,
  inProgressMissions,
  inProgressMissionTotal,
  inProgressMissionFilter,
  onInProgressMissionFilterChange,
  completedMissions,
  completedMissionTotal,
  completedMissionFilter,
  onCompletedMissionFilterChange,
  inProgressBingos,
  inProgressBingoTotal,
  inProgressBingoFilter,
  onInProgressBingoFilterChange,
  completedBingos,
  completedBingoTotal,
  completedBingoFilter,
  onCompletedBingoFilterChange,
  listLimit,
  selectedMissionId,
  selectedBingoId,
  onSelectMission,
  onSelectBingo,
}: MemberDetailPanelProps) => {
  return (
    <S.CenterPanel>
      <S.PanelHeader>유저 상세</S.PanelHeader>
      <S.CenterPanelBody>
        <S.MemberDetailTop>
          <S.MemberCard>
            <S.Avatar />
            <S.MemberInfo>
              <div className="nickname">{member.nickname}</div>
            </S.MemberInfo>
          </S.MemberCard>

          <S.SummaryBoxes>
            <S.SummaryBox>
              <div className="value">{member.missionCompleteCount}</div>
              <div className="label">미션</div>
            </S.SummaryBox>
            <S.SummaryBox>
              <div className="value">{member.bingoCompleteCount}</div>
              <div className="label">빙고</div>
            </S.SummaryBox>
            <S.SummaryBox>
              <div className="value">{member.unclaimedCount}</div>
              <div className="label">미수령</div>
            </S.SummaryBox>
          </S.SummaryBoxes>
        </S.MemberDetailTop>

        <S.MemberDetailLists>
          <S.StatusGroup $variant="inProgress">
            <S.StatusGroupHeader $variant="inProgress">
              <span className="label">진행 중</span>
              <span className="count">
                미션 {inProgressMissionTotal} · 빙고 {inProgressBingoTotal}
              </span>
            </S.StatusGroupHeader>
            <S.StatusGroupBody>
              <FilteredListSection
                title="미션"
                list={inProgressMissions}
                total={inProgressMissionTotal}
                limit={listLimit}
                filter={inProgressMissionFilter}
                onFilterChange={onInProgressMissionFilterChange}
                selectedId={selectedMissionId}
                onSelect={onSelectMission}
              />
              <FilteredListSection
                title="빙고"
                list={inProgressBingos}
                total={inProgressBingoTotal}
                limit={listLimit}
                filter={inProgressBingoFilter}
                onFilterChange={onInProgressBingoFilterChange}
                selectedId={selectedBingoId}
                onSelect={(item) => onSelectBingo(item.id)}
              />
            </S.StatusGroupBody>
          </S.StatusGroup>

          <S.StatusGroup $variant="completed">
            <S.StatusGroupHeader $variant="completed">
              <span className="label">완료</span>
              <span className="count">
                미션 {completedMissionTotal} · 빙고 {completedBingoTotal}
              </span>
            </S.StatusGroupHeader>
            <S.StatusGroupBody>
              <FilteredListSection
                title="미션"
                list={completedMissions}
                total={completedMissionTotal}
                limit={listLimit}
                filter={completedMissionFilter}
                onFilterChange={onCompletedMissionFilterChange}
                selectedId={selectedMissionId}
                onSelect={onSelectMission}
              />
              <FilteredListSection
                title="빙고"
                list={completedBingos}
                total={completedBingoTotal}
                limit={listLimit}
                filter={completedBingoFilter}
                onFilterChange={onCompletedBingoFilterChange}
                selectedId={selectedBingoId}
                onSelect={(item) => onSelectBingo(item.id)}
              />
            </S.StatusGroupBody>
          </S.StatusGroup>
        </S.MemberDetailLists>
      </S.CenterPanelBody>
    </S.CenterPanel>
  );
};

export default MemberDetailPanel;
